<template>
  <layout width="428">
    <template slot="header"> {{ isNew ? 'Add new' : 'Edit' }} filter </template>
    <validation-observer ref="form">
      <v-form class="mx-4" :disabled="isLoading">
        <validation-provider v-slot="{ errors }" name="name" rules="required">
          <v-text-field
            data-test-field="name"
            v-model.trim="name"
            label="Filter name"
            :error-messages="errors"
            clearable
          ></v-text-field>
        </validation-provider>
        <common-schema-selector
          v-model="schemaId"
          :schema-type="schemaType"
          @schema-type="schemaType = $event"
        />
        <subheader>Filters</subheader>
        <div class="d-flex align-center mx-0">
          <div style="font-size: 16px">Linked only</div>
          <v-spacer />
          <v-switch
            class="ma-0 py-3"
            v-model="filters.linkedOnly"
            data-test-switch="linkedOnly"
            hide-details
          ></v-switch>
        </div>
        <v-select     :menu-props="{ bottom: true, offsetY: true }"
                       label="Status" v-model="filters.status"></v-select>
        <v-text-field
          label="Name contains"
          v-model="filters.nameContains"
        ></v-text-field>
        <search-object
          label="Linked to object"
          v-model="filters.linkedToObject"
        />
        <search-object label="Among linked are" v-model="filters.amongLinked" />
      </v-form>
    </validation-observer>
    <v-divider />
    <template #footer>
      <v-spacer />
      <v-btn text @click="close" data-test-action="userCloseAction">
        cancel
      </v-btn>
      <v-btn
        color="primary"
        data-test-action="userCreateOrEditAction"
        :loading="isLoading"
        text
        @click.stop="handleSave"
      >
        <template v-if="isNew">add</template>
        <template v-else>save</template>
      </v-btn>
    </template>
  </layout>
</template>

<script>
// settingsFilters
const filterPropertyExample = [
  {
    id: 'm83DgVT7_WO-ct-sZU5Uc',
    name: 'dfadsfgdf',
    filters: {
      status: null,
      linkedOnly: false,
      amongLinked: null,
      nameContains: null,
      linkedToObject: null
    },
    schemaId: '0d579fc7-5319-4618-9f9d-646ccbe9e209',
    schemaType: 'dataset'
  }
];

import Layout from '@/layouts/LayoutPopup';

import {
  computed,
  ref,
  onMounted,
  reactive,
  toRefs
} from '@vue/composition-api';

import { required } from 'vee-validate/dist/rules';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';

extend('required', {
  ...required,
  message: 'This field is required'
});

import useAuth from '@/api/auth';
import { useApolloClient, useMutation } from '@vue/apollo-composable';

import CommonSchemaSelector from '@/components/app-entity/app-entity-tab/schedule/CommonSchemaSelector.vue';
import SearchObject from '@/components/app-entity/app-entity-tab/schedule/SearchObject.vue';
import gql from '@/api/objects/item.graphql';
import { nanoid } from 'nanoid';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'AddFilterModal',
  props: {
    targetProperty: {
      type: Object,
      default: () => ({})
    },
    entity: {
      type: Object,
      default: () => ({})
    }
  },
  components: {
    SearchObject,
    CommonSchemaSelector,
    Layout,
    ValidationObserver,
    ValidationProvider
  },

  setup(props, { emit }) {
    const { user } = new useAuth();
    const { mutate: updateProperty } = useMutation(gql.updateObjectProperty);

    const form = ref(null);

    const localState = reactive({
      name: '',
      schemaId: null,
      schemaType: 'device',
      filters: {
        linkedOnly: false,
        status: null,
        nameContains: null,
        linkedToObject: null,
        amongLinked: null
      }
    });

    const isNew = computed(() => {
      return !props.entity.id;
    });

    function close() {
      emit('close');
    }

    const createObjectHandler = () => {
      const payload = {
        id: nanoid(),
        name: localState.name,
        schemaType: localState.schemaType,
        schemaId: localState.schemaId,
        filters: localState.filters
      };

      updateProperty({
        input: {
          id: props.targetProperty.id,
          patch: {
            value: [...props.targetProperty.value, payload]
          }
        }
      }).then(() => {
        close();
      });
    };

    const updateObjectHandler = () => {
      let value = cloneDeep(props.targetProperty.value);

      const targetIndex = value.findIndex(item => item.id === props.entity.id);

      const payload = {
        id: props.entity.id,
        name: localState.name,
        schemaId: localState.schemaId,
        schemaType: localState.schemaType,
        filters: localState.filters
      };

      value.splice(targetIndex, 1, payload);

      updateProperty({
        input: {
          id: props.targetProperty.id,
          patch: {
            value: value
          }
        }
      }).then(() => {
        close();
      });
    };

    onMounted(() => {
      if (props.entity.id) {
        const targetFilter = props.targetProperty.value.find(
          item => item.id === props.entity.id
        );
        localState.name = targetFilter.name;
        localState.schemaId = targetFilter.schemaId;
        localState.schemaType = targetFilter.schemaType;
        localState.filters.linkedOnly = targetFilter.filters.linkedOnly;
        localState.filters.amongLinked = targetFilter.filters.amongLinked;
        localState.filters.status = targetFilter.filters.status;
        localState.filters.linkedToObject = targetFilter.filters.linkedToObject;
        localState.filters.nameContains = targetFilter.filters.nameContains;
      }
    });

    const handleSave = () => {
      form.value.validate().then(success => {
        if (!success) {
          return;
        }

        if (isNew.value) {
          createObjectHandler();
        } else {
          updateObjectHandler();
        }
      });
    };

    return {
      ...toRefs(localState),
      isNew,
      close,
      handleSave,
      isLoading: false,
      form
    };
  }
};
</script>
